import React, { useState, useContext } from "react"
import Button from "../atoms/button.atom"
import Switch from "../atoms/switch.atom"
import { TrashIcon, ChatAltIcon, CogIcon } from "@heroicons/react/outline"
import { EndpointConfig } from "../../config"
import { axiosInstance } from "../../lib/axios"
import { toast } from "react-toastify"
import { flatten, parseStringTemplate, validateYup } from "../template-info/templateUtils"
import { ListHubContacts } from "../list-hub-user/list-hub-user.component"
import { Header } from "../common/header"
import Input from "../atoms/input.atom"
import * as yup from "yup"
import Select from "../atoms/select.atom"
import { AuthContext } from "../../context/auth.context"
import { getCookie } from "../../helper/auth"

export const UpdateCampaign = ({ hubId, onUpdateCampaign }) => {
	const { auth } = useContext(AuthContext)
	const selectedConfigData = auth.passData

	const [loading, setLoading] = useState(false)
	const [smsTemplates, setsmsTemplates] = useState([])
	const [wpTemplates, setwpTemplates] = useState([])

	const [selectedContacts, setSelectedContacts] = useState([])
	const [currentTemplate, setCurrentTemplate] = useState({})

	const [form, setForm] = React.useState({
		name: "",
		scheduledOn: "",
		isWhatsApp: false,
		status: "Active"
	})

	const [errors, setErrors] = React.useState({})

	const getSMSTemplates = () => {
		return axiosInstance.get(EndpointConfig.VFSMSTemplates + "?filter=Contact", {
			headers: { "hub-id": hubId }
		})
	}

	const getWPTemplates = () => {
		return axiosInstance.get(EndpointConfig.VFWhatsAppTemplates + "?filter=Contact", {
			headers: { "hub-id": hubId }
		})
	}

	React.useEffect(() => {
		setForm({
			...form,
			name: selectedConfigData.name,
			scheduledOn: selectedConfigData.scheduledOn,
			isWhatsApp: selectedConfigData.isWhatsApp,
			status: selectedConfigData.status
		})

		setLoading(true)
		;(async () => {
			const {
				data: { data: smsTemplateData }
			} = await getSMSTemplates()
			setsmsTemplates(smsTemplateData)

			const { data: wpTemplateData } = await getWPTemplates()
			setwpTemplates(wpTemplateData)

			if (selectedConfigData.isWhatsApp) {
				setCurrentTemplate(
					wpTemplateData.find(
						(template) => template.templateid === selectedConfigData.templateId
					)
				)
			} else {
				setCurrentTemplate(
					smsTemplateData.find(
						(template) => template._id === selectedConfigData.templateId
					)
				)
			}

			if (selectedConfigData.contacts) {
				setSelectedContacts(selectedConfigData.contacts)
			}
		})()
			.catch(console.err)
			.finally(() => setLoading(false))
	}, [])

	const getTemplatePreview = ({ templateText, variablesFlatten }) => {
		try {
			return parseStringTemplate(templateText, variablesFlatten)
		} catch (err) {
			console.error(err)
			return templateText
		}
	}

	const updateCampaign = () => {
		if (!(selectedContacts && selectedContacts.length > 0)) {
			toast("Please select at least one contact")
			return
		}

		const today = new Date()

		let schema = yup.object().shape({
			name: yup
				.string()
				.trim()
				.required("Required")
				.matches(
					/^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
					"Only alphabets are allowed for this field "
				),
			scheduledOn: yup.date().required("Required").min(today, "Date should not be in past"),
			status: yup.string().trim().required("Required")
		})

		validateYup(schema, form)
			.then(() => {
				setLoading(true)
				;(async () => {
					const reqContacts = selectedContacts.map((cont) => {
						const contVariablesFlatten = flatten("contact", cont)

						const text = form.isWhatsApp
							? currentTemplate.templatetext
							: currentTemplate.text

						return {
							phone: cont.phone,
							text: getTemplatePreview({
								templateText: text,
								variablesFlatten: contVariablesFlatten
							}),
							hs_object_id: cont.hs_object_id,
							name: cont.firstname || cont.name
						}
					})

					const url = EndpointConfig.Campaign + "/" + selectedConfigData._id
					const templateId = form.isWhatsApp
						? currentTemplate.templateid
						: currentTemplate._id

					const reqObj = {
						...form,
						contacts: reqContacts,
						templateId
					}

					const token = getCookie("token")
					await axiosInstance.put(
						url,
						{
							...reqObj
						},
						{
							headers: {
								Authorization: `Bearer ${token}`
							}
						}
					)
					toast("Campaign updated successfully")
					setSelectedContacts([])
					setCurrentTemplate({})
					onUpdateCampaign?.()
				})()
					.catch((err) => {
						console.error(err)
						if (err?.response?.data?.message) {
							toast(err.response.data.message)
						} else {
							toast("something went wrong")
						}
					})
					.finally(() => setLoading(false))
			})
			.catch(setErrors)
	}

	const differenceInArray = (arr1, arr2, key) => {
		return arr1.filter((item) => !arr2.find((item2) => item2[key] === item[key]))
	}

	const onContactSelect = (cont) => {
		const newSelectedContact = differenceInArray(cont, selectedContacts, "hs_object_id")

		if (newSelectedContact.length > 0) {
			setSelectedContacts([...selectedContacts, ...newSelectedContact])
		}
	}

	const removeSelectedContact = ({ hs_object_id }) => {
		setSelectedContacts(selectedContacts.filter(({ hs_object_id: id }) => id !== hs_object_id))
	}

	return (
		<>
			<div className="flex flex-col">
				<Header
					IconName={CogIcon}
					subText={"Setup"}
					mainText={"Update Campaign"}
					postComponent={
						<Switch
							label={"Whats App"}
							toggle={form?.isWhatsApp}
							onToggleClick={() => setForm({ ...form, isWhatsApp: !form.isWhatsApp })}
						/>
					}
				/>{" "}
				<div className="flex flex-row gap-4">
					<div className="flex flex-col w-3/12 p-4 bg-white border-r border-gray-200 border-solid rounded ">
						<div className="mt-2 text-xs text-gray-500 uppercase">Search & Choose</div>
						<ListHubContacts hubId={hubId} onContactSelect={onContactSelect} />
					</div>

					<div className="flex flex-col flex-1 p-4 bg-white rounded">
						{selectedContacts.length > 0 && (
							<>
								<label className="text-xs text-gray-500 uppercase">
									Selected Contacts
								</label>
								<div className="flex flex-row flex-wrap gap-2 mt-1 mb-3">
									{selectedContacts.map((cont) => {
										return (
											<div
												key={cont?.phone}
												className="flex flex-row p-1 px-3 text-xs bg-gray-200 rounded-full"
											>
												<span className="mr-2">
													{cont?.firstname || cont?.name} : {cont?.phone}
												</span>
												<TrashIcon
													className="w-4 h-4 text-gray-500 cursor-pointer"
													onClick={() => removeSelectedContact(cont)}
												/>
											</div>
										)
									})}
								</div>

								<Input
									name="name"
									error={errors?.name}
									value={form.name}
									label={"Campaign Name"}
									placeholder={"Campaign Name"}
									onChange={(e) => setForm({ ...form, name: e.target.value })}
								/>

								<div className="flex flex-row gap-6">
									<Input
										name="scheduledOn"
										error={errors?.scheduledOn}
										value={form.scheduledOn}
										type="datetime-local"
										label={"Scheduled On"}
										placeholder={""}
										onChange={(e) =>
											setForm({ ...form, scheduledOn: e.target.value })
										}
									/>
									<Select
										placeholder={"Status"}
										error={errors?.status}
										label="Status"
										selectedValue={{
											label: form.status,
											value: form.status
										}}
										onSelect={({ value }) => {
											setForm({ ...form, status: value })
										}}
										options={["Active", "InActive"].map((ob) => ({
											label: ob,
											value: ob
										}))}
									/>
								</div>

								{form?.isWhatsApp ? (
									<>
										<label className="mt-4 text-xs text-gray-500 uppercase">
											Choose WhatsApp template
										</label>
										<select
											className="w-full px-2 py-1 text-sm border border-solid border-blue"
											value={currentTemplate?.templateid}
											onChange={(e) => {
												setCurrentTemplate(
													wpTemplates.find(
														(t) => t.templateid === e.target.value
													) || {}
												)
											}}
										>
											<option>Select</option>
											{wpTemplates.map((template) => (
												<option
													key={template.templateid}
													value={template.templateid}
												>
													{template.name} - {template.templatename}
												</option>
											))}
										</select>

										{currentTemplate?.templateid && (
											<div className="flex flex-col my-4 mt-8">
												<label className="text-xs text-gray-500 uppercase">
													Template Text
												</label>
												<div className="mb-2">
													{currentTemplate.templatetext}
												</div>

												<p className="mt-4 text-xs text-gray-400">
													This message will sent via Sender Phone:{" "}
													<b>{currentTemplate.senderPhone}</b>
												</p>

												<div className="flex flex-row gap-6">
													<div className="flex-[3]">&nbsp;</div>
													<div className="flex-1">
														&nbsp;
														{loading ? (
															<Button className="text-black bg-red-100 pointer-events-none">
																Loading
															</Button>
														) : (
															<Button
																className="w-full"
																onClick={updateCampaign}
															>
																Update Campaign
															</Button>
														)}
													</div>
												</div>
											</div>
										)}
									</>
								) : (
									<>
										<label className="mt-4 text-xs text-gray-500 uppercase">
											Choose SMS template
										</label>
										<select
											className="w-full px-2 py-1 text-sm border border-solid border-blue"
											value={currentTemplate?._id}
											onChange={(e) => {
												setCurrentTemplate(
													smsTemplates.find(
														(t) => t._id === e.target.value
													) || {}
												)
											}}
										>
											<option>Select</option>
											{smsTemplates.map((template) => (
												<option key={template._id} value={template._id}>
													{template.name}
												</option>
											))}
										</select>

										{currentTemplate?._id && (
											<div className="flex flex-col my-4 mt-8">
												<label className="text-xs text-gray-500 uppercase">
													Template Text
												</label>
												<div className="mb-2">{currentTemplate.text}</div>

												<p className="mt-4 text-xs text-gray-400">
													This message will sent via AccountId:{" "}
													<b>{currentTemplate.userId}</b> and SenderId:{" "}
													<b>{currentTemplate.senderId}</b>
													<br />
													having DTL TemplateId as :{" "}
													<b>{currentTemplate.dtlTemplateId}</b>{" "}
												</p>

												<div className="flex flex-row gap-6">
													<div className="flex-[3]">&nbsp;</div>
													<div className="flex-1">
														&nbsp;
														{loading ? (
															<Button className="text-black bg-red-100 pointer-events-none">
																Loading
															</Button>
														) : (
															<Button
																className="w-full"
																onClick={updateCampaign}
															>
																Update Campaign
															</Button>
														)}
													</div>
												</div>
											</div>
										)}
									</>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</>
	)
}
